import React from 'react';
import { useNavigate } from 'react-router-dom';
import Wordmark from '../components/Wordmark';
import MetaLabel from '../components/MetaLabel';
import { tokens } from '../tokens';

/* ── Viewport hook ── */
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = React.useState(() => window.innerWidth >= 900);
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 900px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

/* ── NavGlyph (inline SVGs for four rooms) ── */
function NavGlyph({ kind, size = 20, color = 'currentColor' }) {
  const s = size;
  const style = { display: 'block' };
  const sw = 1.3;
  if (kind === 'stage') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" style={style} fill="none">
        <path d="M3 5h18M3 5v14h3V8c2 1 4 1 6 0 2-1 4-1 6 0v11h3V5"
          stroke={color} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round"/>
        <path d="M10 19v-8M14 19v-8" stroke={color} strokeWidth={sw * 0.8} strokeLinecap="round" opacity="0.5"/>
      </svg>
    );
  }
  if (kind === 'green') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" style={style} fill="none">
        <path d="M4 13 V9 a2 2 0 0 1 2 -2 h12 a2 2 0 0 1 2 2 v4"
          stroke={color} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round"/>
        <path d="M3 13 a1.5 1.5 0 0 1 1.5 -1.5 h15 A1.5 1.5 0 0 1 21 13 v4 H3 Z"
          stroke={color} strokeWidth={sw} strokeLinejoin="round"/>
        <path d="M6 17 v2 M18 17 v2" stroke={color} strokeWidth={sw} strokeLinecap="round"/>
      </svg>
    );
  }
  if (kind === 'hours') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" style={style} fill="none">
        <path d="M19 15.5A8 8 0 019.5 4 8 8 0 1019 15.5z"
          stroke={color} strokeWidth={sw} strokeLinejoin="round"/>
        <circle cx="15.5" cy="7.5" r="0.9" fill={color} opacity="0.5"/>
        <circle cx="17" cy="11" r="0.6" fill={color} opacity="0.5"/>
      </svg>
    );
  }
  if (kind === 'kit') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" style={style} fill="none">
        <path d="M9 7 V6 a2 2 0 0 1 2 -2 h2 a2 2 0 0 1 2 2 v1"
          stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="3.5" y="7" width="17" height="12" rx="1.5"
          stroke={color} strokeWidth={sw} strokeLinejoin="round"/>
        <path d="M3.5 11 H20.5" stroke={color} strokeWidth={sw} opacity="0.7"/>
        <path d="M11 11 V13 H13 V11" stroke={color} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round"/>
      </svg>
    );
  }
  return null;
}

/* ── Principle row (mobile) ── */
function Principle({ number, title, body }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 16 }}>
      <div style={{ fontFamily: tokens.fontCode, fontSize: 13, letterSpacing: '0.1em', color: tokens.accent, paddingTop: 4 }}>
        {number}
      </div>
      <div>
        <div style={{ fontFamily: tokens.fontDisplay, fontSize: 24, color: tokens.textHi, letterSpacing: tokens.displayTracking, lineHeight: 1.3 }}>
          {title}
        </div>
        <div style={{ fontFamily: tokens.fontBody, fontSize: 16, color: tokens.textMid, marginTop: 8, lineHeight: 1.6, textWrap: 'pretty' }}>
          {body}
        </div>
      </div>
    </div>
  );
}

/* ── Room data ── */
const ROOMS = [
  ['stage', 'The Stage',      'A chronological feed. Vent, ask, celebrate, lurk.'],
  ['green', 'The Green Room', 'Private journaling with post-show prompts.'],
  ['hours', 'Ghost Hours',    'End-to-end encrypted 1:1 with a peer in your trade.'],
  ['kit',   'The Toolkit',    'Crisis lines, union EAPs, therapists who get the life.'],
];

/* ════════════════════════════════════════════════════════════
   MOBILE LAYOUT (< 900px)
   ════════════════════════════════════════════════════════════ */
function MobileLanding({ navigate }) {
  return (
    <div style={{ position: 'relative', minHeight: '100%', paddingBottom: 40 }}>
      <div className="gl-atmosphere" />
      <div className="gl-grain" />

      {/* Top bar */}
      <div style={{ padding: '18px 22px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', position: 'relative', zIndex: 2 }}>
        <Wordmark tokens={tokens} size={11} amber />
      </div>

      {/* Hero */}
      <div style={{ padding: '0 22px 0', position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{
          position: 'relative',
          width: 280, height: 280,
          marginTop: -18,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: `gl-fade 1400ms ${tokens.ease}`,
        }}>
          {/* Ambient bloom */}
          <div aria-hidden="true" style={{
            position: 'absolute',
            left: '50%', top: '40%',
            transform: 'translate(-50%, -50%)',
            width: 220, height: 220,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${tokens.accent}55 0%, ${tokens.accent}22 35%, transparent 70%)`,
            mixBlendMode: 'screen',
            pointerEvents: 'none',
            filter: 'blur(18px)',
            animation: 'gl-flicker-orb 5.4s ease-in-out infinite',
          }} />
          {/* Lamp photo */}
          <img src="/assets/ghostlight-lamp.png" alt=""
            style={{
              position: 'relative',
              width: '100%', height: '100%',
              objectFit: 'contain', display: 'block',
              animation: 'gl-lamp-flicker 3.1s ease-in-out infinite',
            }} />
        </div>

        <div style={{ marginTop: -8, textAlign: 'center', animation: `gl-fade-up 900ms ${tokens.ease} 300ms both` }}>
          <h1 style={{
            fontFamily: tokens.fontDisplay,
            fontSize: 46, lineHeight: 1.08, fontWeight: tokens.displayWeight,
            letterSpacing: tokens.displayTracking,
            color: tokens.textHi, margin: 0,
            textWrap: 'balance',
          }}>
            The light<br/>that stays on.
          </h1>
          <p style={{
            fontFamily: tokens.fontDisplay, fontStyle: 'italic',
            fontSize: 17, lineHeight: 1.55,
            color: tokens.textMid, marginTop: 22, marginBottom: 0,
            textWrap: 'pretty',
          }}>
            A place for the people<br/>
            who keep the show running —<br/>
            when the house is dark and everyone's gone home.
          </p>
        </div>
      </div>

      {/* Split pill CTA */}
      <div style={{ padding: '48px 22px 0', position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{
          display: 'flex',
          border: `1px solid ${tokens.accent}`,
          borderRadius: 999,
          overflow: 'hidden',
          background: tokens.accentGlow,
          maxWidth: 340,
          width: '100%',
        }}>
          <button className="gl-btn-bare" onClick={() => navigate('/register')} style={{
            flex: 1,
            padding: '15px 10px',
            color: tokens.accent,
            fontFamily: tokens.fontBody, fontSize: 14, fontWeight: 500, letterSpacing: '0.04em',
            textAlign: 'center',
            borderRight: `1px solid ${tokens.accent}44`,
            background: 'transparent',
            transition: `background 300ms ${tokens.ease}`,
          }}
            onMouseEnter={e => e.currentTarget.style.background = `${tokens.accent}22`}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            Create account
          </button>
          <button className="gl-btn-bare" onClick={() => navigate('/login')} style={{
            flex: 1,
            padding: '15px 10px',
            color: tokens.textMid,
            fontFamily: tokens.fontBody, fontSize: 14, letterSpacing: '0.04em',
            textAlign: 'center',
            background: 'transparent',
            transition: `color 300ms ${tokens.ease}`,
          }}
            onMouseEnter={e => e.currentTarget.style.color = tokens.textHi}
            onMouseLeave={e => e.currentTarget.style.color = tokens.textMid}
          >
            Sign in
          </button>
        </div>

        {/* Scroll hint */}
        <button className="gl-btn-bare" onClick={() => {
          const el = document.getElementById('gl-landing-below');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }} style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 8, margin: '38px auto 0',
          color: tokens.textLow,
          fontFamily: tokens.fontCode, fontSize: 10, letterSpacing: '0.22em',
          textTransform: 'uppercase',
          animation: 'gl-scroll-hint 2.6s ease-in-out infinite',
        }}
          onMouseEnter={e => e.currentTarget.style.color = tokens.textMid}
          onMouseLeave={e => e.currentTarget.style.color = tokens.textLow}
        >
          <span>more below</span>
          <svg width="14" height="18" viewBox="0 0 14 18" fill="none" aria-hidden="true">
            <path d="M7 2v12M2 10l5 5 5-5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* What this is */}
      <div id="gl-landing-below" style={{ padding: '80px 22px 0', position: 'relative', zIndex: 2 }}>
        <hr className="gl-hair" />
        <MetaLabel tokens={tokens} style={{ marginTop: 40, marginBottom: 20, color: tokens.accent }}>What this is</MetaLabel>
        <p style={{
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 22, lineHeight: 1.45, color: tokens.textHi, margin: 0,
          textWrap: 'pretty',
        }}>
          Peer support for technicians, stage managers, dressers, performers — anyone whose job ends at 2am and who has nowhere to put how the show affected them.
        </p>

        <div style={{ marginTop: 64, display: 'flex', flexDirection: 'column', gap: 36 }}>
          <Principle number="01" title="Pseudonymous, not anonymous"
            body="Your real name is ours — admin-only, for safety. Everyone you meet sees a department-derived alias like FresnelWraith or CueSpecter." />
          <Principle number="02" title="No likes. No algorithm. No ads."
            body="Chronological feed. No engagement metrics. Nobody's getting famous here. We'll never sell anything to anyone." />
          <Principle number="03" title="Peer support, not therapy"
            body="We'll point you to real clinicians when you need one. But most nights, what you need is someone who's been in the same loading dock." />
        </div>
      </div>

      {/* Four rooms */}
      <div style={{ padding: '80px 22px 0', position: 'relative', zIndex: 2 }}>
        <hr className="gl-hair" />
        <MetaLabel tokens={tokens} style={{ marginTop: 40, marginBottom: 28, color: tokens.accent }}>Four rooms, one building</MetaLabel>
        {ROOMS.map(([, name, desc], i) => (
          <div key={name} style={{
            padding: '18px 0', borderBottom: `1px solid ${tokens.edge}`,
            animation: `gl-fade-up 600ms ${tokens.ease} ${i * 80}ms both`,
          }}>
            <div style={{ fontFamily: tokens.fontDisplay, fontSize: 26, color: tokens.textHi, letterSpacing: tokens.displayTracking }}>
              {name}
            </div>
            <div style={{ fontFamily: tokens.fontBody, fontSize: 16, color: tokens.textMid, marginTop: 6, lineHeight: 1.55 }}>
              {desc}
            </div>
          </div>
        ))}
      </div>

      {/* Closing */}
      <div style={{ padding: '80px 22px 24px', position: 'relative', zIndex: 2, textAlign: 'center' }}>
        <p style={{
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 22, lineHeight: 1.4, color: tokens.textHi, margin: 0,
        }}>
          You're not the only one<br/>still standing in the dark.
        </p>
        <button className="gl-btn-bare" onClick={() => navigate('/register')} style={{
          marginTop: 28,
          padding: '15px 34px',
          border: `1px solid ${tokens.accent}`,
          borderRadius: 999,
          color: tokens.accent,
          fontFamily: tokens.fontBody, fontSize: 13, fontWeight: 500, letterSpacing: '0.18em',
          textTransform: 'uppercase',
          background: tokens.accentGlow,
        }}>Create account</button>
      </div>

      {/* Footer */}
      <div style={{ padding: '40px 22px 20px', position: 'relative', zIndex: 2, borderTop: `1px solid ${tokens.edge}` }}>
        <div style={{ display: 'flex', gap: 20, fontFamily: tokens.fontCode, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: tokens.textLow }}>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/terms')}>Terms</span>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/privacy')}>Privacy</span>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/transparency')}>Transparency</span>
        </div>
        <div style={{ marginTop: 18, fontFamily: tokens.fontCode, fontSize: 10, letterSpacing: '0.12em', color: tokens.textDim }}>
          Free, forever. Maintained by NINJA.
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   DESKTOP LAYOUT (>= 900px)
   ════════════════════════════════════════════════════════════ */
function DesktopLanding({ navigate }) {
  return (
    <div style={{
      height: '100%', overflowY: 'auto', overflowX: 'hidden',
      background: tokens.ground, color: tokens.textHi,
      fontFamily: tokens.fontBody, position: 'relative',
    }}>
      <div className="gl-grain" />

      {/* Top strip — wordmark left, sign in right */}
      <div style={{
        padding: '22px 40px', position: 'relative', zIndex: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 12,
          fontFamily: tokens.fontCode, fontSize: 15,
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: tokens.accent, fontWeight: 500,
        }}>
          <span style={{
            width: 9, height: 9, borderRadius: '50%', background: tokens.accent,
            boxShadow: `0 0 14px ${tokens.accent}`,
            animation: 'gl-breathe 3.5s ease-in-out infinite',
          }} />
          ghostlight
        </div>
        <button className="gl-btn-bare" onClick={() => navigate('/login')} style={{
          color: tokens.textMid,
          fontFamily: tokens.fontBody, fontSize: 13, letterSpacing: '0.04em',
        }}
          onMouseEnter={e => e.currentTarget.style.color = tokens.textHi}
          onMouseLeave={e => e.currentTarget.style.color = tokens.textMid}
        >Sign in &rarr;</button>
      </div>

      {/* Hero — giant wordmark, centered */}
      <section style={{
        minHeight: 'min(calc(100vh - 70px), 720px)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '40px 80px 60px',
        position: 'relative', zIndex: 2,
        maxWidth: 1280, margin: '0 auto',
        overflow: 'hidden', textAlign: 'center',
      }}>
        {/* Soft overhead glow */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `radial-gradient(ellipse 60% 60% at 50% 40%, ${tokens.accentGlow} 0%, transparent 60%)`,
          zIndex: 0,
        }} />

        {/* GIANT wordmark with pulsing dot */}
        <div style={{
          position: 'relative', zIndex: 1,
          display: 'inline-flex', alignItems: 'center',
          gap: 'clamp(36px, 4.2vw, 64px)',
          fontFamily: tokens.fontCode,
          fontSize: 'clamp(56px, 9vw, 124px)',
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: tokens.accent, fontWeight: 500,
          animation: `gl-fade 1400ms ${tokens.ease}`,
        }}>
          <span aria-hidden="true" style={{
            width: 'clamp(34px, 5.2vw, 70px)',
            height: 'clamp(34px, 5.2vw, 70px)',
            borderRadius: '50%', background: tokens.accent,
            boxShadow: `0 0 48px ${tokens.accent}, 0 0 120px ${tokens.accent}66`,
            animation: 'gl-breathe 3.5s ease-in-out infinite',
            flexShrink: 0,
          }} />
          <span>ghostlight</span>
        </div>

        {/* Tagline */}
        <p style={{
          position: 'relative', zIndex: 1,
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 'clamp(20px, 2vw, 26px)', lineHeight: 1.5,
          color: tokens.textMid, margin: 0,
          marginTop: 'clamp(32px, 4vw, 52px)',
          textWrap: 'balance', maxWidth: 680,
          animation: `gl-fade-up 900ms ${tokens.ease} 300ms both`,
        }}>
          The light that stays on. A place for the people who keep the show running — when the house is dark and everyone's gone home.
        </p>

        {/* CTAs — pill button + plain text, side by side */}
        <div style={{
          position: 'relative', zIndex: 1,
          marginTop: 'clamp(36px, 4vw, 52px)',
          display: 'flex', gap: 14, alignItems: 'center',
          animation: `gl-fade-up 900ms ${tokens.ease} 500ms both`,
        }}>
          <button className="gl-btn-bare" onClick={() => navigate('/register')} style={{
            padding: '16px 34px',
            border: `1px solid ${tokens.accent}`,
            borderRadius: 999,
            color: tokens.accent,
            background: tokens.accentGlow,
            fontFamily: tokens.fontBody, fontSize: 14, fontWeight: 500, letterSpacing: '0.04em',
            transition: `all 300ms ${tokens.ease}`,
          }}
            onMouseEnter={e => e.currentTarget.style.background = `${tokens.accent}22`}
            onMouseLeave={e => e.currentTarget.style.background = tokens.accentGlow}
          >Create account</button>
          <button className="gl-btn-bare" onClick={() => navigate('/login')} style={{
            padding: '16px 28px',
            color: tokens.textMid,
            fontFamily: tokens.fontBody, fontSize: 14, letterSpacing: '0.04em',
          }}
            onMouseEnter={e => e.currentTarget.style.color = tokens.textHi}
            onMouseLeave={e => e.currentTarget.style.color = tokens.textMid}
          >Sign in</button>
        </div>
      </section>

      {/* What this is — two-column */}
      <section style={{
        maxWidth: 1040, margin: '0 auto',
        padding: '20px 80px 0',
        position: 'relative', zIndex: 2,
      }}>
        <hr style={{ border: 'none', borderTop: `1px solid ${tokens.edge}`, margin: 0 }} />
        <div style={{
          marginTop: 56,
          display: 'grid', gridTemplateColumns: '200px 1fr', gap: 60,
          alignItems: 'start',
        }}>
          <div style={{
            fontFamily: tokens.fontCode, fontSize: 15, letterSpacing: '0.22em',
            textTransform: 'uppercase', color: tokens.accent,
          }}>What this is</div>
          <p style={{
            fontFamily: tokens.fontDisplay, fontStyle: 'italic',
            fontSize: 26, lineHeight: 1.4, color: tokens.textHi, margin: 0,
            textWrap: 'pretty', maxWidth: 640,
          }}>
            Peer support for technicians, stage managers, dressers, performers — anyone whose job ends at 2am and who has nowhere to put how the show affected them.
          </p>
        </div>

        {/* Principles — 3-column grid */}
        <div style={{
          marginTop: 80,
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 40,
          alignItems: 'start',
        }}>
          {[
            ['01', <>Pseudonymous.<br/>Not anonymous.</>, "Your real name is ours — admin-only, for safety. Everyone you meet sees a department-derived alias like FresnelWraith or CueSpecter."],
            ['02', <>No likes. No ads.<br/>No algorithms.</>, "Chronological feed. No engagement metrics. Nobody's getting famous here. We'll never sell anything to anyone."],
            ['03', <>Peer support,<br/>not therapy.</>, "We'll point you to real clinicians when you need one. But most nights, what you need is someone who's been in the same loading dock."],
          ].map(([num, title, body]) => (
            <div key={num}>
              <div style={{
                fontFamily: tokens.fontCode, fontSize: 13, letterSpacing: '0.1em',
                color: tokens.accent, marginBottom: 14,
              }}>{num}</div>
              <div style={{
                fontFamily: tokens.fontDisplay, fontSize: 26,
                color: tokens.textHi, letterSpacing: tokens.displayTracking,
                lineHeight: 1.25, marginBottom: 16,
                minHeight: 'calc(26px * 1.25 * 2)',
              }}>{title}</div>
              <div style={{
                fontFamily: tokens.fontBody, fontSize: 16, color: tokens.textMid,
                lineHeight: 1.65, textWrap: 'pretty',
              }}>{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Four rooms — two-column with icons */}
      <section style={{
        maxWidth: 1040, margin: '0 auto',
        padding: '100px 80px 0',
        position: 'relative', zIndex: 2,
      }}>
        <hr style={{ border: 'none', borderTop: `1px solid ${tokens.edge}`, margin: 0 }} />
        <div style={{
          marginTop: 56,
          display: 'grid', gridTemplateColumns: '200px 1fr', gap: 60,
          alignItems: 'start',
        }}>
          <div style={{
            fontFamily: tokens.fontCode, fontSize: 15, letterSpacing: '0.22em',
            textTransform: 'uppercase', color: tokens.accent,
          }}>Four rooms,<br/>one building</div>
          <div>
            {ROOMS.map(([icon, name, desc], i) => (
              <div key={name} style={{
                padding: '22px 0', borderBottom: `1px solid ${tokens.edge}`,
                display: 'grid', gridTemplateColumns: '28px 200px 1fr', gap: 28,
                alignItems: 'center',
                animation: `gl-fade-up 600ms ${tokens.ease} ${i * 80}ms both`,
              }}>
                <NavGlyph kind={icon} size={32} color={tokens.accent} />
                <div style={{
                  fontFamily: tokens.fontDisplay, fontSize: 26, color: tokens.textHi,
                  letterSpacing: tokens.displayTracking, lineHeight: 1.2,
                }}>{name}</div>
                <div style={{
                  fontFamily: tokens.fontBody, fontSize: 16, color: tokens.textMid,
                  lineHeight: 1.55, textWrap: 'pretty',
                }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section style={{
        maxWidth: 1040, margin: '0 auto',
        padding: '120px 80px 80px',
        position: 'relative', zIndex: 2,
        textAlign: 'center',
      }}>
        <p style={{
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 28, lineHeight: 1.4, color: tokens.textHi,
          margin: '0 auto', maxWidth: 640, textWrap: 'balance',
        }}>
          You're not the only one<br/>still standing in the dark.
        </p>
        <button className="gl-btn-bare" onClick={() => navigate('/register')} style={{
          marginTop: 36,
          padding: '15px 40px',
          border: `1px solid ${tokens.accent}`,
          borderRadius: 999,
          color: tokens.accent,
          background: tokens.accentGlow,
          fontFamily: tokens.fontBody, fontSize: 14, fontWeight: 500, letterSpacing: '0.04em',
        }}>Create account</button>
        <div style={{
          marginTop: 60,
          fontFamily: tokens.fontCode, fontSize: 10, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: tokens.textMid,
        }}>Free, forever. Maintained by NINJA.</div>
      </section>

      {/* Footer */}
      <div style={{ padding: '40px 80px 30px', position: 'relative', zIndex: 2, borderTop: `1px solid ${tokens.edge}`, textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', fontFamily: tokens.fontCode, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: tokens.textLow }}>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/terms')}>Terms</span>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/privacy')}>Privacy</span>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/transparency')}>Transparency</span>
        </div>
      </div>
    </div>
  );
}

/* ── Landing page (V2 responsive) ── */
export default function Landing() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  return isDesktop
    ? <DesktopLanding navigate={navigate} />
    : <MobileLanding navigate={navigate} />;
}
